import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { apiGet, apiPut } from "utils/apiFetch";
import apiPath from "utils/pathObj";
import useToastContext from "hooks/useToastContext";
import ConfirmationModal from "components/reusable/ConfirmationModal";

const OnboardingGeoIps = ({ role, title, help }) => {
  const { t } = useTranslation();
  const notification = useToastContext();
  const [policy, setPolicy] = useState(null);
  const [ipValue, setIpValue] = useState("");
  const [ipLabel, setIpLabel] = useState("");
  const [publicIp, setPublicIp] = useState("");
  const [saving, setSaving] = useState(false);
  const [removeConfirm, setRemoveConfirm] = useState(null);

  const load = async () => {
    try {
      const res = await apiGet(apiPath.geoAccessPolicy);
      if (res?.data?.success) setPolicy(res.data.results);
    } catch (err) {
      notification.error(err?.response?.data?.message || t("GEO_LOAD_FAILED"));
    }
  };

  useEffect(() => {
    load();
    fetch("https://api.ipify.org?format=json")
      .then((r) => r.json())
      .then((data) => setPublicIp(String(data?.ip || "").trim()))
      .catch(() => {});
  }, []);

  const rows = (policy?.ipAllowlist || []).filter(
    (i) => (i.role || "any") === role && !i.userId,
  );

  const blockedRows = (policy?.ipBlocklist || []).filter(
    (i) => (i.role || "any") === role && !i.userId,
  );

  const savePolicy = async (partial) => {
    try {
      setSaving(true);
      const res = await apiPut(apiPath.geoAccessPolicy, { ...policy, ...partial });
      if (res?.data?.success) {
        setPolicy(res.data.results);
        notification.success(res.data.message || t("GEO_SAVED"));
      } else notification.error(res?.data?.message || t("GEO_SAVE_FAILED"));
    } catch (err) {
      notification.error(err?.response?.data?.message || t("GEO_SAVE_FAILED"));
    }
    setSaving(false);
  };

  const addIp = (value, label) => {
    const ip = (value || "").trim();
    if (!ip) return;
    const list = policy?.ipAllowlist || [];
    if (list.some((i) => i.value === ip && (i.role || "any") === role && !i.userId)) {
      notification.error(t("GEO_IP_EXISTS"));
      return;
    }
    savePolicy({
      ipAllowlist: [...list, { value: ip, label: (label || "").trim(), role }],
      ipBlocklist: (policy?.ipBlocklist || []).filter(
        (i) => !(i.value === ip && (i.role || "any") === role && !i.userId),
      ),
    });
    setIpValue("");
    setIpLabel("");
  };

  const blockIp = (value, label) => {
    const ip = (value || "").trim();
    if (!ip) return;
    const list = policy?.ipBlocklist || [];
    if (list.some((i) => i.value === ip && (i.role || "any") === role && !i.userId)) {
      notification.error(t("GEO_IP_EXISTS"));
      return;
    }
    savePolicy({
      ipBlocklist: [...list, { value: ip, label: (label || "").trim() || t("GEO_SECTION_BLOCKED"), role }],
      ipAllowlist: (policy?.ipAllowlist || []).filter(
        (i) => !(i.value === ip && (i.role || "any") === role && !i.userId),
      ),
    });
    setIpValue("");
    setIpLabel("");
  };

  const removeIp = (value) => {
    savePolicy({
      ipAllowlist: (policy?.ipAllowlist || []).filter((i) => !(i.value === value && (i.role || "any") === role && !i.userId)),
    });
  };

  const removeBlockedIp = (value) => {
    savePolicy({
      ipBlocklist: (policy?.ipBlocklist || []).filter(
        (i) => !(i.value === value && (i.role || "any") === role && !i.userId),
      ),
    });
  };

  if (!policy) return null;

  return (
    <div className="px-4 py-4 border-b border-[#E3E3E3]">
      <h4 className="font-semibold mb-1">{title}</h4>
      <p className="text-sm text-gray-500 mb-3">{help}</p>
      <div className="flex flex-wrap gap-2 mb-3">
        <input
          className="border rounded-lg px-3 py-2"
          placeholder={t("GEO_IP_PLACEHOLDER")}
          value={ipValue}
          onChange={(e) => setIpValue(e.target.value)}
        />
        <input
          className="border rounded-lg px-3 py-2"
          placeholder={t("GEO_LABEL")}
          value={ipLabel}
          onChange={(e) => setIpLabel(e.target.value)}
        />
        <button
          type="button"
          className="bg-LightBlue text-white px-4 py-2 rounded-lg"
          onClick={() => addIp(ipValue, ipLabel)}
          disabled={saving}
        >
          {t("GEO_ADD_IP")}
        </button>
        <button
          type="button"
          className="border border-red-400 text-red-600 px-4 py-2 rounded-lg"
          onClick={() => blockIp(ipValue, ipLabel)}
          disabled={saving}
        >
          {t("GEO_BLOCK_IP")}
        </button>
        {(publicIp || policy.currentIp) && (
          <button
            type="button"
            className="border px-4 py-2 rounded-lg"
            onClick={() => addIp(publicIp || policy.currentIp, t("GEO_THIS_COMPUTER"))}
            disabled={saving}
          >
            {t("GEO_ALLOW_MY_IP")}
          </button>
        )}
      </div>
      <div className="border rounded-lg overflow-hidden">
        {rows.length === 0 ? (
          <div className="p-3 text-sm text-gray-400">{t("GEO_NONE")}</div>
        ) : (
          rows.map((row) => (
            <div
              key={`${row.role || ""}-${row.value}`}
              className="flex justify-between items-center px-3 py-2 border-b last:border-b-0 text-sm"
            >
              <span>
                {row.value} {row.label ? `— ${row.label}` : ""}
              </span>
              <button type="button" className="text-red-500" onClick={() => setRemoveConfirm({ name: row.value, run: () => removeIp(row.value) })}>
                Remove
              </button>
            </div>
          ))
        )}
      </div>
      <h4 className="font-semibold mt-4 mb-1">{t("GEO_SECTION_BLOCKED")}</h4>
      <p className="text-sm text-gray-500 mb-2">{t("GEO_SECTION_BLOCKED_HELP")}</p>
      <div className="border rounded-lg overflow-hidden">
        {(blockedRows || []).length === 0 ? (
          <div className="p-3 text-sm text-gray-400">{t("GEO_NONE")}</div>
        ) : (
          blockedRows.map((row) => (
            <div
              key={`block-${row.value}`}
              className="flex justify-between items-center px-3 py-2 border-b last:border-b-0 text-sm"
            >
              <span>
                {row.value} {row.label ? `— ${row.label}` : ""}
              </span>
              <button type="button" className="text-red-500" onClick={() => setRemoveConfirm({ name: row.value, run: () => removeBlockedIp(row.value) })}>
                Remove
              </button>
            </div>
          ))
        )}
      </div>
      <ConfirmationModal
        isOpen={!!removeConfirm}
        onClose={() => setRemoveConfirm(null)}
        onConfirm={() => {
          removeConfirm?.run?.();
          setRemoveConfirm(null);
        }}
        isDanger
        title="Remove?"
        message={`Do you still want to remove ${removeConfirm?.name || "this"}?`}
        confirmText="Yes, remove"
        cancelText="Cancel"
      />
    </div>
  );
};

export default OnboardingGeoIps;
